import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import type { PaymentMethodsType } from '@/constants/paymentMethods'

interface PaymentMethodItemProps extends PaymentMethodsType {
  onPress: (paymentMethod: PaymentMethodsType) => void
  isDisabled?: boolean
  _shouldDisplayCurrencyIcon?: boolean // Prefix _ for unused parameter
  hasOnlyOnePaymentMethod?: boolean
}

export const PaymentMethodItem: React.FC<PaymentMethodItemProps> = ({
  onPress,
  isDisabled = false,
  _shouldDisplayCurrencyIcon = false,
  hasOnlyOnePaymentMethod = false,
  ...item
}) => {
  const theme = useTheme()

  const isWallet = Boolean(
    item.paymentMethod === 'Wallet' && item.displayCurrencyCode,
  )

  const handlePress = () => {
    if (!isDisabled) {
      onPress(item)
    }
  }

  return (
    <BaseView
      row
      align="center"
      gap={16}
      px={16}
      py={16}
      bgColor="background.cardItemRoot"
      rounded={26}
      style={{
        cursor: isDisabled ? 'default' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        transition: 'all 0.2s ease',
      }}
      className={!isDisabled ? 'hover:opacity-80' : ''}
      onClick={handlePress}
    >
      {/* Payment Method Icon */}
      {isWallet ? (
        <CardItem.CurrencyIcon
          uri={item.currencyIconUrl}
          size={48}
          bgColor={
            isDisabled
              ? theme.colors.background.screen
              : Array.isArray(item.currencyBgColor)
                ? item.currencyBgColor[0] || '#171819'
                : item.currencyBgColor || '#171819'
          }
          fallbackText={item.displayCurrencyCode?.substring(0, 2) || '?'}
        />
      ) : (
        <BaseView
          center
          style={{
            width: '62px',
            height: '50px',
            borderRadius: '16px', // Rounded as in RN
            // Exactly as in RN: bgIconContainer or fallback "#171819"
            background:
              typeof item.bgIconContainer === 'string'
                ? item.bgIconContainer
                : Array.isArray(item.bgIconContainer)
                  ? `#171819`
                  : '#171819',
          }}
        >
          {item.iconName && (
            <AppIcons
              name={item.iconName}
              size={item.iconSize || 24}
              color="white" // In RN "icon.activeInputIcon" is used which is white
            />
          )}
        </BaseView>
      )}

      {/* Payment Method Info */}
      <BaseView flex={1} gap={4}>
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          color={isDisabled ? 'label.disabled' : 'label.default'}
        >
          {isWallet ? `${item.currencyName} ${item.name}` : item.name}
        </BaseText>
        {isWallet && (
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color={isDisabled ? 'label.disabled' : 'label.description'}
          >
            {item.formattedAmountBalance || ''}
          </BaseText>
        )}
      </BaseView>

      {/* Chevron Right */}
      {!hasOnlyOnePaymentMethod && (
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{
            color: isDisabled
              ? theme.colors.label.disabled
              : theme.colors.label.description,
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      )}
    </BaseView>
  )
}

export default PaymentMethodItem
